import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { toCdn, toModel } from '../utils/cdn';
import * as THREE from 'three';
import { ImageBitmapLoader } from 'three';
import './Model.css';
import { InfoPanel3D } from './InfoPanel';
import { projectMeta } from '../projects/data';
import { useBrowserInfo } from '../hooks/useBrowserInfo';

// Feature flags for screen shader effects (set to true to enable)
const SCANLINES_ENABLED = false;
const GLITCH_ENABLED = false; // disable glitch to reduce per-frame sampling cost
const REFLECTIONS_ENABLED = true;

// Heuristic: detect Safari (exclude Chrome/Android). Used to avoid ImageBitmapLoader on Safari per
// https://github.com/mrdoob/three.js/issues/30416#issuecomment-2820706080
const isSafari = (typeof navigator !== 'undefined') && /safari/i.test(navigator.userAgent) && !/chrome|android/i.test(navigator.userAgent);

// Texture needsUpdate override removed for now
// Disposal helpers
// Close underlying ImageBitmap (when present) before disposing textures/materials to reduce memory
const disposeMap = (map) => {
  if (!map) return;
  try {
    if (map.image && typeof map.image.close === 'function') {
      map.image.close();
    }
  } catch (_) {}
  try { map.dispose && map.dispose(); } catch (_) {}
};

const disposeMaterial = (material) => {
  if (!material) return;
  const materials = Array.isArray(material) ? material : [material];
  materials.forEach((mat) => {
    if (!mat) return;

    // Common material maps
    disposeMap(mat.map);
    disposeMap(mat.lightMap);
    disposeMap(mat.bumpMap);
    disposeMap(mat.normalMap);
    disposeMap(mat.specularMap);
    disposeMap(mat.envMap);
    disposeMap(mat.alphaMap);
    disposeMap(mat.aoMap);
    disposeMap(mat.displacementMap);
    disposeMap(mat.emissiveMap);
    disposeMap(mat.gradientMap);
    disposeMap(mat.metalnessMap);
    disposeMap(mat.roughnessMap);

    // Fallback: iterate any other texture-like props
    try {
      Object.keys(mat).forEach((key) => {
        const tex = mat[key];
        if (tex && tex.isTexture) disposeMap(tex);
      });
    } catch (_) {}

    try { mat.dispose && mat.dispose(); } catch (_) {}
  });
};

// Helper to safely dispose of Three.js objects
const disposeObject = (obj) => {
  if (!obj) return;

  if (obj.geometry) {
    try { obj.geometry.dispose(); } catch (_) {}
  }

  if (obj.material) {
    disposeMaterial(obj.material);
  }

  if (obj.dispose && typeof obj.dispose === 'function') {
    try { obj.dispose(); } catch (_) {}
  }
};

// Clone a scene and also deep‑clone mesh materials to avoid mutating GLTF cache across mounts
// https://discourse.threejs.org/t/proof-that-gltfloader-does-not-free-memory-in-safari-that-it-frees-in-chrome/75201/28
const cloneSceneWithMaterials = (srcScene) => {
  if (!srcScene) return null;
  const cloned = srcScene.clone(true);
  try {
    cloned.traverse((child) => {
      if (child && child.isMesh && child.material) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m) => (m && m.clone ? m.clone() : m));
        } else if (child.material.clone) {
          child.material = child.material.clone();
        }
      }
    });
  } catch (_) {}
  return cloned;
};

// Recursively dispose scene graph
const disposeNode = (node) => {
  if (!node) return;

  if (node.children) {
    [...node.children].forEach(child => disposeNode(child));
  }

  disposeObject(node);

  if (node.parent) {
    node.parent.remove(node);
  }
};

// Safari-safe texture disposal with microtask delay
const disposeSafely = (items, delay = 0) => {
  const dispose = () => {
    items.forEach(item => {
      if (item && typeof item.dispose === 'function') {
        try {
          // If this is a texture with a closeable ImageBitmap, close it first
          if (item.image && typeof item.image.close === 'function') {
            try { item.image.close(); } catch (_) {}
          }
          item.dispose();
        } catch (e) {
          console.warn('Disposal error:', e);
        }
      }
    });
  };

  if (delay > 0 || isSafari) {
    // Use microtask for Safari or when delay requested
    Promise.resolve().then(dispose);
  } else {
    dispose();
  }
};

function makeGroundTexture({ size = 1024, base = '#ffffff', noise = 0.0, fadeRadius = 0.0 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');

  // 1) Base fill (light, neutral)
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  // 3) Create alpha mask with smoother, horizon‑biased falloff
  const cx = size * 0.5;
  // push the gradient center a bit below center so the top (far horizon) fades sooner
  const cy = size * 0.65;
  const maxR = size * 0.6;

  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - cx;
      const dy = y - cy;
      // Elliptical bias: stretch Y so it fades out faster toward the top of the texture
      const dyBias = dy * 1.2;
      const distance = Math.sqrt(dx * dx + dyBias * dyBias);
      let alpha = 1 - (distance / maxR);
      // Strengthen near center and smooth the edge with improved smoothstep
      if (alpha < 0) alpha = 0; else if (alpha > 1) alpha = 1;
      // Use smoother easing function for more gradual transition
      const alphaSmooth = alpha * alpha * alpha * (alpha * (alpha * 6 - 15) + 10); // smootherstep

      const index = (y * size + x) * 4 + 3; // Alpha channel
      data[index] = Math.floor(alphaSmooth * 255);
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}


// Ground plane component
function GroundPlane() {
  const groundTexture = useMemo(() =>
    makeGroundTexture({
      size: 1024,
      base: '#f5f5f5',
      noise: 0,
      fadeRadius: 0.0
    }),
    []
  );

  // Removed ground texture disposal for now

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.8, 0]}
      receiveShadow
    >
      <planeGeometry args={[20, 20]} />
      <meshLambertMaterial
        map={groundTexture}
        transparent={true}
        alphaMap={groundTexture}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}


// Mac model component using React Three Fiber
function MacModel() {
  const { scene: srcScene } = useGLTF('/images/models/macintosh.glb');
  const scene = useMemo(() => cloneSceneWithMaterials(srcScene), [srcScene]);
  const sceneRef = useRef(scene);
  useEffect(() => { sceneRef.current = scene; }, [scene]);

  useEffect(() => {
    if (scene) {
      if (scene.userData.__normalized) return;
      // Reset transforms to a known baseline before computing bounds
      scene.position.set(0, 0, 0);
      scene.rotation.set(0, 0, 0);
      scene.scale.set(1, 1, 1);
      scene.updateMatrixWorld(true);
      // Calculate bounding box to center and scale the model properly
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Center the model
      scene.position.sub(center);

      // Scale the model to a reasonable size (max dimension = 2 units)
      const maxDimension = Math.max(size.x, size.y, size.z);
      const scale = 2 / maxDimension;
      scene.scale.setScalar(scale);

      // Position the model at center, but adjust Y to account for model's base
      scene.position.set(0, -size.y * scale * 0.5 + 0.17, 0);

      // Enable shadow casting for all meshes in the model
      scene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;

          // Ensure geometry has correct lighting data
          const geometry = child.geometry;
          if (geometry) {
            // Recompute vertex normals to avoid dark/shaded artifacts on newly added parts
            geometry.computeVertexNormals();
            // If an AO map is present but uv2 is missing, clone uv into uv2
            if (!geometry.attributes.uv2 && geometry.attributes.uv) {
              geometry.setAttribute('uv2', new THREE.BufferAttribute(geometry.attributes.uv.array, 2));
            }
          }

          // Clean up materials without adding noise
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                if (mat) {
                mat.transparent = false;
                  mat.alphaTest = 0;
                  if (mat.isMeshStandardMaterial) {
                    if (mat.metalness !== undefined) mat.metalness = 0.0;
                    if (mat.roughness !== undefined) mat.roughness = 0.65;
                    if (mat.envMapIntensity !== undefined) mat.envMapIntensity = Math.min(mat.envMapIntensity, 0.65);
                    mat.side = THREE.FrontSide;
                    mat.shadowSide = THREE.FrontSide;
                  }
                }
              });
            } else {
              child.material.transparent = false;
              child.material.alphaTest = 0;
              if (child.material.isMeshStandardMaterial) {
                if (child.material.metalness !== undefined) child.material.metalness = 0.0;
                if (child.material.roughness !== undefined) child.material.roughness = 0.65;
                if (child.material.envMapIntensity !== undefined) child.material.envMapIntensity = Math.min(child.material.envMapIntensity, 0.65);
                child.material.side = THREE.FrontSide;
                child.material.shadowSide = THREE.FrontSide;
              }
            }
          }
        }
      });
      scene.userData.__normalized = true;
    }
  }, [scene]);

  // Ensure textures/materials rebind after mount (Chrome route return)
  useEffect(() => {
    if (!scene) return;
    try {
      scene.traverse((child) => {
        if (child.isMesh && child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((mat) => {
            try {
              if (mat.map) { mat.map.needsUpdate = true; }
              mat.needsUpdate = true;
            } catch (_) {}
          });
        }
      });
    } catch (_) {}
  }, [scene]);

  // Dispose scene on unmount
  useEffect(() => {
    return () => {
      try { disposeNode(scene); } catch (e) { console.warn('MacModel dispose error:', e); }
    };
  }, [scene]);

  return <primitive object={scene} castShadow receiveShadow />;
}

// Mug model component using React Three Fiber
function MugModel() {
  const { scene: srcScene } = useGLTF(toModel('/images/models/mug.glb'));
  const scene = useMemo(() => cloneSceneWithMaterials(srcScene), [srcScene]);
  const sceneRef = useRef(scene);
  useEffect(() => { sceneRef.current = scene; }, [scene]);

  useEffect(() => {
    if (scene) {
      if (scene.userData.__normalized) return;
      scene.position.set(0, 0, 0);
      scene.rotation.set(0, 0, 0);
      scene.scale.set(1, 1, 1);
      scene.updateMatrixWorld(true);
      // Calculate bounding box to center and scale the model properly
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Center the model
      scene.position.sub(center);

      // Scale the model to a reasonable prop size on desk
      const maxDimension = Math.max(size.x, size.y, size.z);
      const scale = 0.75 / maxDimension;
      scene.scale.setScalar(scale);

      // Place on the ground plane to the left/front of the Mac
      const halfHeight = (size.y * scale) * 0.125;
      scene.position.set(-1.45, -0.8 + halfHeight - 0.075, 0.25);
      // Add a slight rotation so the mug faces the viewer a bit
      scene.rotation.set(0, -3.25, 0);

      // Enable shadows and basic material cleanup
      scene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                if (mat) {
                  mat.transparent = false;
                  mat.alphaTest = 0;
                  // Ensure physically-based response to light/shadows
                  if (mat.metalness !== undefined) mat.metalness = 0.0;
                  if (mat.roughness !== undefined) mat.roughness = 0.0;
                  mat.side = THREE.FrontSide;
                  mat.shadowSide = THREE.FrontSide;
                }
              });
            } else {
              child.material.transparent = false;
              child.material.alphaTest = 0;
              if (child.material.metalness !== undefined) child.material.metalness = 0.0;
              if (child.material.roughness !== undefined) child.material.roughness = 0.0;
              child.material.side = THREE.FrontSide;
              child.material.shadowSide = THREE.FrontSide;
            }
          }
        }
      });
      scene.userData.__normalized = true;
    }
  }, [scene]);

  // Dispose scene on unmount
  useEffect(() => {
    // Avoid disposing mug assets to prevent shared texture invalidation on route changes (Chrome)
    return () => {};
  }, [scene]);

  return <primitive object={scene} castShadow receiveShadow />;
}

// Screen model component using React Three Fiber
function ScreenModel({ selectedTexture, rippleAmount = 0, isZoomed = false, screenFadeAmount = 0, introActive = false, onAnchorReady = undefined, mobilePerfMode = false, scanlinesEnabled = SCANLINES_ENABLED, glitchEnabled = GLITCH_ENABLED, reflectionsEnabled = REFLECTIONS_ENABLED, isVisible = true, lightsOn = true, onReadyForDisplay = undefined }) {
  const { scene: srcScene } = useGLTF(toModel('/images/models/screen.glb'));
  const scene = useMemo(() => cloneSceneWithMaterials(srcScene), [srcScene]);
  const pointLightRef = useRef();
  const readySentRef = useRef(false);
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [texturesVersion, setTexturesVersion] = useState(0);
  const rippleStartTimeRef = useRef(null);
  const lastRippleAmountRef = useRef(0);
  const rippleCompletedRef = useRef(false);
  const screenFadeRef = useRef(0);
  const localFadeRef = useRef(0);
  const fadeAnimRef = useRef({ active: false, startMs: 0, dir: 1 });
  // One-time normalization of screen transforms to prevent cumulative re-scaling on HMR/breakpoints
  useEffect(() => {
    if (!scene) return;
    if (scene.userData.__normalized) return;
    // Reset transforms before computing bounds
    scene.position.set(0, 0, 0);
    scene.rotation.set(0, 0, 0);
    scene.scale.set(1, 1, 1);
    scene.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Center the model
    scene.position.sub(center);

    // Scale to target size (match prior logic 1.1/maxDimension)
    const maxDimension = Math.max(size.x, size.y, size.z);
    const scale = 1.1 / maxDimension;
    scene.scale.setScalar(scale);

    // Place at intended height
    scene.position.set(0, -size.y * scale * 0.7 + -0.179, 0.025);

    scene.userData.__normalized = true;
  }, [scene]);

  // Removed disposal/clear on unmount for now
  useEffect(() => () => {}, [scene]);


// (Debug logging removed)
  useEffect(() => {}, [scene]);

// Helper: detach CPU-side image data after first GPU upload (disabled)
const detachTextureSourceAfterUpload = (tex) => {
  if (!tex) return;
  tex.onUpdate = null;
};

// Global texture cache to persist across route transitions
const sharedTextureCache = new Map();

// Util: load a texture (TextureLoader on Safari, ImageBitmapLoader otherwise) with cache
const loadBitmapTexture = (src, onLoad) => {
  if (sharedTextureCache.has(src)) {
    return sharedTextureCache.get(src);
  }
  if (isSafari) {
    const loader = new THREE.TextureLoader();
    const tex = loader.load(src, (loaded) => {
      try { detachTextureSourceAfterUpload(loaded); } catch(_){}
      if (onLoad) onLoad(loaded, loaded.image);
    });
    try { sharedTextureCache.set(src, tex); } catch(_) {}
    return tex;
  } else {
    const ibl = new ImageBitmapLoader();
    try { ibl.setOptions && ibl.setOptions({ imageOrientation: 'none', premultiplyAlpha: 'none' }); } catch (_) {}
    const tex = new THREE.Texture();
    try { sharedTextureCache.set(src, tex); } catch(_) {}
    ibl.load(src, (bitmap) => {
      try { tex.image = bitmap; tex.needsUpdate = true; } catch(_){ }
      try { detachTextureSourceAfterUpload(tex); } catch(_){ }
      if (onLoad) onLoad(tex, bitmap);
    });
    return tex;
  }
};

  // Texture cache; start with only the default texture to cap memory
  const texturesRef = useRef({});
  // Track last non-default texture to avoid flashing default on mobile during loads
  const lastChosenTextureRef = useRef(null);
  useEffect(() => {
    let mounted = true;
    const defaultTex = loadBitmapTexture(toCdn('/images/screens/marketplace.png'), (t) => {
      if (mounted) setTexturesLoaded(true);
    });
    defaultTex.flipY = false;
    defaultTex.wrapS = THREE.ClampToEdgeWrapping;
    defaultTex.wrapT = THREE.ClampToEdgeWrapping;
    defaultTex.minFilter = THREE.LinearFilter;
    defaultTex.magFilter = THREE.LinearFilter;
    defaultTex.generateMipmaps = false;
    texturesRef.current['default'] = defaultTex;
    setTexturesVersion((v) => v + 1);
    scanLineUniformsRef.current.uTexture.value = defaultTex;

    return () => { mounted = false; };
  }, []);

  // Lazily load the currently selected texture; dispose others to cap memory
  useEffect(() => {
    if (!selectedTexture || selectedTexture === 'default') return;
    const meta = projectMeta[selectedTexture];
    if (!meta?.screenImage) return;
    if (texturesRef.current[selectedTexture]) return;
    const tex = loadBitmapTexture(toCdn(meta.screenImage));
    tex.flipY = false;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    texturesRef.current[selectedTexture] = tex;
    setTexturesVersion((v) => v + 1);

    // Skip disposing other textures for now
  }, [selectedTexture]);
  // Keep latest fade amount in a ref for use inside the animation loop
  useEffect(() => {
    screenFadeRef.current = screenFadeAmount || 0.0;
  }, [screenFadeAmount]);

  // Local fallback fade animation driven purely by isZoomed
  useEffect(() => {
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    fadeAnimRef.current.active = true;
    fadeAnimRef.current.startMs = now;
    fadeAnimRef.current.dir = isZoomed ? 1 : -1; // 1 fades in to black, -1 fades out to image
  }, [isZoomed]);


  // Get current texture based on selected texture
  const currentTexture = useMemo(() => {
    const texMap = texturesRef.current;
    let chosen = (selectedTexture && texMap[selectedTexture]) ? texMap[selectedTexture] : texMap['default'];
    // On mobile, if a project is selected but not yet loaded, keep last non-default texture
    if (mobilePerfMode && selectedTexture && !texMap[selectedTexture] && lastChosenTextureRef.current && lastChosenTextureRef.current !== texMap['default']) {
      chosen = lastChosenTextureRef.current;
    }
    if (chosen && chosen.image) chosen.needsUpdate = true;
    return chosen;
  }, [selectedTexture, texturesLoaded, texturesVersion, mobilePerfMode]);

  // (moved below material creation to avoid TDZ)

  // Create shared uniforms for scan line shader (stable ref)
  const scanLineUniformsRef = useRef({
    uTexture: { value: null },
    uTime: { value: 0 },
    uScanLineIntensity: { value: (mobilePerfMode || !scanlinesEnabled) ? 0.0 : 0.04 },
    uScanLineSpeed: { value: -3.0 }, // slightly slower
    uScanLineCount: { value: 180 }, // fewer lines for lower cost
    uGlitchIntensity: { value: 0.0 },
    uGlitchTime: { value: 0.0 },
    uColor: { value: new THREE.Color(0xffffff) },
    uEmissiveColor: { value: new THREE.Color(0x0066ff) },
    uEmissiveIntensity: { value: 0.0 },
    uGlassStrength: { value: 1.0 },
    uGlassTint: { value: new THREE.Color(0.96, 0.98, 1.0) },
    uFresnelBias: { value: 0.1 },
    uFresnelPower: { value: 2.0 },
    uFresnelScale: { value: 0.75 },
    uEnvMap: { value: null },
    uEnvIntensity: { value: (mobilePerfMode || !reflectionsEnabled) ? 0.0 : 0.4 },
    uRoughness: { value: 0.2 },
    uRippleAmount: { value: 0.0 },
    uRipplePhase: { value: 0.0 },
    uBlurAmount: { value: 0.0 },
    uFadeToBlack: { value: 0.0 }
  });

  // Load a simple equirectangular environment texture for reflections
  const envTexture = useMemo(() => {
    if (mobilePerfMode || !reflectionsEnabled) return null;
    const tex = loadBitmapTexture(toCdn('/images/reflection.webp'));
    try { tex.colorSpace = THREE.LinearSRGBColorSpace; } catch(_){}
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    return tex;
  }, [mobilePerfMode, reflectionsEnabled]);

  // Moved below material creation to avoid TDZ errors

  // Track previous texture to detect changes
  const previousTextureRef = useRef(null);

  // Create scan line shader material
  const scanLineMaterial = useMemo(() => {
    const material = new THREE.ShaderMaterial({
      uniforms: scanLineUniformsRef.current,
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vWorldPos;
        varying vec3 vNormal;
        void main() {
          vUv = uv;
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPos.xyz;
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform float uTime;
        uniform float uScanLineIntensity;
        uniform float uScanLineSpeed;
        uniform float uScanLineCount;
        uniform float uGlitchIntensity;
        uniform float uGlitchTime;
        uniform vec3 uColor;
        uniform vec3 uEmissiveColor;
        uniform float uEmissiveIntensity;
        uniform float uGlassStrength;
        uniform vec3 uGlassTint;
        uniform float uFresnelBias;
        uniform float uFresnelPower;
        uniform float uFresnelScale;
        uniform sampler2D uEnvMap; // equirectangular env
        uniform float uEnvIntensity;
        uniform float uRoughness;
        uniform float uRippleAmount;
        uniform float uRipplePhase;
        uniform float uBlurAmount;
        uniform float uFadeToBlack;
        varying vec2 vUv;
        varying vec3 vWorldPos;
        varying vec3 vNormal;

        // Random function for glitch effect
        float random(vec2 st) {
          return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
        }

        // Sample equirectangular env map with a reflection direction
        vec3 sampleEquirect(sampler2D map, vec3 dir) {
          // dir must be normalized
          float PI = 3.141592653589793;
          float phi = atan(dir.z, dir.x);
          float theta = asin(clamp(dir.y, -1.0, 1.0));
          vec2 uv = vec2(0.5 + (phi / (2.0*PI)), 0.5 - (theta / PI));
          return texture2D(map, uv).rgb;
        }

        void main() {
          vec2 uv = vUv;

          // Scale texture down slightly by adjusting UV coordinates
          // This makes the texture smaller and centered on the screen
          uv.x = (uv.x - 0.505) * 0.925 + 0.5; // Scale down horizontally
          uv.y = (uv.y - 0.5) * 1.1 + 0.5; // Scale down vertically

          // Ripple distortion (single expanding ring)
          vec2 rippleUv = uv;
          float eps = 1e-4;
          vec2 cp = (rippleUv - vec2(0.5)) * 2.0;
          float cl = length(cp) + eps;
          if (uRippleAmount > 0.0) {
            // Single ring centered at uRipplePhase in [0..1], thicker width and slightly stronger displacement
            float bandWidth = 0.14; // thicker ring
            float ring = 1.0 - smoothstep(0.0, bandWidth, abs(cl - uRipplePhase));
            rippleUv += (cp / cl) * ring * 0.065 * uRippleAmount; // slightly stronger displacement
          }

          // Sample the base texture with clamped UVs (already linear)
          vec2 clampedUv = clamp(rippleUv, 0.0, 1.0);

          // Base sample and optional blur (skip heavy taps when blur==0)
          vec4 baseSample = texture2D(uTexture, clampedUv);
          vec4 baseColor = baseSample;
          if (uBlurAmount > 0.001) {
            float r = 0.004 * clamp(uBlurAmount, 0.0, 1.0);
            vec2 o1 = vec2(r, 0.0);
            vec2 o2 = vec2(0.0, r);
            vec2 o3 = vec2(r, r);
            vec4 sum = baseSample * 4.0;
            sum += texture2D(uTexture, clamp(clampedUv + o1, 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv - o1, 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv + o2, 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv - o2, 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv + o3, 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv - o3, 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv + vec2(-r, r), 0.0, 1.0));
            sum += texture2D(uTexture, clamp(clampedUv + vec2(r, -r), 0.0, 1.0));
            vec4 blurredSample = sum / 12.0;
            baseColor = mix(baseSample, blurredSample, clamp(uBlurAmount, 0.0, 1.0));
          }

          // Create animated scan lines
          float scanLine = sin(vUv.y * uScanLineCount + uTime * uScanLineSpeed) * 0.5 + 0.5;
          scanLine = pow(scanLine, 2.0); // Make scan lines more defined

          // Glitch effect: subtle horizontal RGB split driven by time and line noise (skip when intensity==0)
          vec3 glitchColor = baseColor.rgb;
          if (uGlitchIntensity > 0.001) {
            float lineNoise = random(vec2(floor(vUv.y * 400.0), uGlitchTime * 10.0));
            float jitter = (lineNoise - 0.5) * 0.03 * uGlitchIntensity;
            vec2 uvR = clampedUv + vec2(jitter, 0.0);
            vec2 uvG = clampedUv;
            vec2 uvB = clampedUv - vec2(jitter, 0.0);
            glitchColor = vec3(
              texture2D(uTexture, clamp(uvR, 0.0, 1.0)).r,
              texture2D(uTexture, clamp(uvG, 0.0, 1.0)).g,
              texture2D(uTexture, clamp(uvB, 0.0, 1.0)).b
            );
          }

          // Combine base texture with scan lines (no color tinting or emissive glow)
          vec3 finalColor = glitchColor;
          finalColor += scanLine * uScanLineIntensity;
          // Noise removed

          // View and normal
          vec3 N = normalize(vNormal);
          vec3 V = normalize(cameraPosition - vWorldPos);
          float cosTheta = max(dot(N, V), 0.0);
          float fres = uFresnelBias + (1.0 - uFresnelBias) * pow(1.0 - cosTheta, uFresnelPower);
          fres = clamp(fres * uFresnelScale, 0.0, 1.0);

          // Environment reflection (skip sampling when intensity==0)
          vec3 envCol = vec3(0.0);
          if (uEnvIntensity > 0.001) {
            vec3 R = reflect(-V, N);
            envCol = sampleEquirect(uEnvMap, normalize(R));
          }

          // Mix env using fresnel and glass strength
          finalColor = mix(finalColor, envCol, uEnvIntensity * uGlassStrength * fres);

          // Add a subtle tinted fresnel highlight on top
          vec3 glassFX = uGlassTint * fres * 0.5;
          finalColor += glassFX * uGlassStrength;

          finalColor = clamp(finalColor, 0.0, 1.0);
          // Fade to white based on zoom progress
          finalColor = mix(finalColor, vec3(1.0), clamp(uFadeToBlack, 0.0, 1.0));
          gl_FragColor = vec4(finalColor, baseColor.a);
        }
      `,
      side: THREE.FrontSide,
      transparent: false,
      depthTest: true,
      depthWrite: true,
      toneMapped: false
    });
    // Default render order; rely on depth testing
    return material;
  }, []);

  // After material exists, safely assign env map and flag update
  useEffect(() => {
    if (!scanLineMaterial) return;
    scanLineUniformsRef.current.uEnvMap.value = envTexture || null;
    scanLineMaterial.needsUpdate = true;
  }, [envTexture, scanLineMaterial]);

  // Notify host when first texture and material are bound so page can reveal
  useEffect(() => {
    if (readySentRef.current) return;
    if (!scene || !scanLineMaterial) return;
    const tex = currentTexture;
    if (!tex || !tex.image) return;
    readySentRef.current = true;
    try { if (typeof onReadyForDisplay === 'function') onReadyForDisplay(); } catch(_) {}
  }, [scene, scanLineMaterial, currentTexture, onReadyForDisplay]);

  // React to mobile perf mode changes by updating uniforms
  useEffect(() => {
    const uniforms = scanLineUniformsRef.current;
    uniforms.uScanLineIntensity.value = (mobilePerfMode || !scanlinesEnabled) ? 0.0 : 0.04;
    uniforms.uEnvIntensity.value = (mobilePerfMode || !reflectionsEnabled) ? 0.0 : 0.4;
    uniforms.uGlitchIntensity.value = 0.0; // reset glitch
    uniforms.uBlurAmount.value = 0.0; // ensure no blur on mobile
    if (scanLineMaterial) scanLineMaterial.needsUpdate = true;
  }, [mobilePerfMode, scanLineMaterial, scanlinesEnabled, reflectionsEnabled]);


  // Animation loop for scan lines and point light updates
  const glitchWasActiveRef = useRef(false);

  useFrame((state) => {
    if (!isVisible) return;
    // Update the shared time uniform
    const tNow = state.clock.elapsedTime;
    scanLineUniformsRef.current.uTime.value = tNow;

    // Single-shot ripple: detect rising edge to start, advance once, then stop
    if (lastRippleAmountRef.current <= 0 && rippleAmount > 0) {
      rippleStartTimeRef.current = tNow;
      rippleCompletedRef.current = false;
    }
    if (rippleAmount <= 0) {
      rippleStartTimeRef.current = null;
      rippleCompletedRef.current = false;
    }

    let phase = rippleCompletedRef.current ? 1.0 : 0.0;
    if (rippleStartTimeRef.current !== null && !rippleCompletedRef.current) {
      const duration = 0.5; // slower ripple pass
      phase = Math.min(1.0, Math.max(0.0, (tNow - rippleStartTimeRef.current) / duration));
      if (phase >= 1.0) {
        rippleCompletedRef.current = true;
      }
    }
    scanLineUniformsRef.current.uRipplePhase.value = phase;
    const effectiveRipple = rippleCompletedRef.current ? 0.0 : rippleAmount * 1.2; // boost thickness/strength
    scanLineUniformsRef.current.uRippleAmount.value = effectiveRipple;
    lastRippleAmountRef.current = rippleAmount;

    // Disable shader blur
    scanLineUniformsRef.current.uBlurAmount.value = 0.0;

    // Advance local fallback fade when an isZoomed transition occurs
    if (fadeAnimRef.current.active) {
      const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const durationMs = 500;
      const t = Math.min(1, Math.max(0, (nowMs - fadeAnimRef.current.startMs) / durationMs));
      const eased = t * t * (3 - 2 * t);
      localFadeRef.current = fadeAnimRef.current.dir > 0 ? eased : (1 - eased);
      if (t >= 1) fadeAnimRef.current.active = false;
    }

    // Apply fade-to-white using the stronger of parent-provided and local fallback
    // but disable during intro so the texture is visible behind the intro overlay
    const combinedFade = Math.max(screenFadeRef.current || 0.0, localFadeRef.current || 0.0);
    scanLineUniformsRef.current.uFadeToBlack.value = introActive ? 0.0 : combinedFade;

    // Update texture when it changes
    if (previousTextureRef.current !== currentTexture) {

      // Only update if the texture has image data
      if (currentTexture && currentTexture.image) {
        scanLineUniformsRef.current.uTexture.value = currentTexture;
        currentTexture.needsUpdate = true;
        previousTextureRef.current = currentTexture;

        // Force material update
        if (scanLineMaterial) {
          scanLineMaterial.needsUpdate = true;
        }
      } else {
        // texture not ready; skip
      }
    }

    // Glitch timing: brief glitch every few seconds
    if (glitchEnabled) {
      const t2 = state.clock.elapsedTime;
      const glitchInterval = 5.0;
      const glitchDuration = 0.35;
      const timeSinceLastGlitch = t2 % glitchInterval;
      const isGlitching = !isZoomed && (timeSinceLastGlitch < glitchDuration) && !mobilePerfMode;
      scanLineUniformsRef.current.uGlitchIntensity.value = isGlitching ? 0.9 : 0.0;
      scanLineUniformsRef.current.uGlitchTime.value = t2;
      if (glitchWasActiveRef.current !== isGlitching) {
        glitchWasActiveRef.current = isGlitching;
      }
    } else {
      scanLineUniformsRef.current.uGlitchIntensity.value = 0.0;
    }

    // Update point light for default glow
    if (pointLightRef.current) {
      pointLightRef.current.color.setHex(0x0066ff);
      pointLightRef.current.intensity = lightsOn ? 1.5 : 0.0;
      pointLightRef.current.distance = 2.5;
    }
  });

  useEffect(() => {
    if (scene && texturesLoaded) {
      if (!scene.userData.__normalized) {
        // Calculate bounding box to center and scale the model properly
        const box = new THREE.Box3().setFromObject(scene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Center the model
        scene.position.sub(center);

        // Scale the model to a reasonable size (match initial normalization)
        const maxDimension = Math.max(size.x, size.y, size.z);
        const scale = 1.1 / maxDimension;
        scene.scale.setScalar(scale);

        // Position the screen model at the same Y level as the Mac model
        scene.position.set(0, -size.y * scale * 0.7 + -0.15, 0.025);
        scene.userData.__normalized = true;
      }


       // debug logs removed

      // Collect meshes and choose the most likely screen surface (curved mesh)
      const meshes = [];
      scene.traverse((child) => { if (child.isMesh) meshes.push(child); });

      const nameHints = ['screen', 'display', 'glass', 'monitor', 'crt'];
      const scoreMesh = (mesh) => {
        let score = 0;
        const name = (mesh.name || '').toLowerCase();
        if (nameHints.some(h => name.includes(h))) score += 1000;
        const hasUV = !!mesh.geometry?.attributes?.uv;
        if (hasUV) score += 500;
        const posCount = mesh.geometry?.attributes?.position?.count || 0;
        score += Math.floor(posCount / 50);
        return score;
      };

      const targetMesh = meshes.sort((a, b) => scoreMesh(b) - scoreMesh(a))[0];

      if (targetMesh) {
        targetMesh.castShadow = true;
        targetMesh.receiveShadow = true;
        targetMesh.material = scanLineMaterial;
        targetMesh.renderOrder = 0; // rely on depth testing
        targetMesh.visible = true;
        scanLineUniformsRef.current.uColor.value.setHex(0xffffff);
        // applied scanline material

        // Create or update a HUD anchor that follows the screen mesh
        try {
          const mesh = targetMesh;
          const anchor = new THREE.Object3D();
          anchor.name = 'InfoPanelAnchor';

          // Compute local bounding box for relative placement
          if (mesh.geometry && !mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
          }
          const bb = mesh.geometry?.boundingBox;
          if (bb) {
            const center = new THREE.Vector3();
            const size = new THREE.Vector3();
            bb.getCenter(center);
            bb.getSize(size);
            // Place near top-right/front of the screen surface in local space
            anchor.position.copy(center);
            anchor.position.x += size.x * 0.45;
            anchor.position.y += size.y * 0.35;
            anchor.position.z += size.z * 0.02;
          } else {
            // Fallback position relative to mesh origin
            anchor.position.set(0.3, 0.2, 0.02);
          }

          // Slight rotation to face outward from screen
          anchor.rotation.set(0, 0, 0);

          // Attach anchor to the screen mesh so it inherits its transform
          mesh.add(anchor);

          if (typeof onAnchorReady === 'function') {
            onAnchorReady(anchor);
          }
        } catch (e) {
          //
        }
      } else {
        //
      }
     }
  }, [scene, scanLineMaterial, scanLineUniformsRef, texturesLoaded]);

  // Separate effect to handle texture updates without recreating scene setup
  useEffect(() => {
    if (!currentTexture || !currentTexture.image) return;
    // Ensure linear color space for textures
    if (currentTexture.colorSpace !== THREE.LinearSRGBColorSpace) {
      currentTexture.colorSpace = THREE.LinearSRGBColorSpace;
    }
    // Only update the texture uniform, don't recreate the scene
    scanLineUniformsRef.current.uTexture.value = currentTexture;
    if (scanLineMaterial) {
      scanLineMaterial.needsUpdate = true;
    }
  }, [currentTexture, scanLineMaterial]);

  // Remember last chosen non-default texture when it becomes valid (has image)
  useEffect(() => {
    if (currentTexture && currentTexture.image) {
      const texMap = texturesRef.current;
      if (currentTexture !== texMap['default']) {
        lastChosenTextureRef.current = currentTexture;
      }
    }
  }, [currentTexture]);

  // Re-bind texture on window resize to avoid blanking after resize
  useEffect(() => {
    const rebind = () => {
      const tex = currentTexture;
      if (tex && tex.image) {
        scanLineUniformsRef.current.uTexture.value = tex;
        tex.needsUpdate = true;
        if (scanLineMaterial) scanLineMaterial.needsUpdate = true;
      }
    };
    window.addEventListener('resize', rebind);
    return () => window.removeEventListener('resize', rebind);
  }, [currentTexture, scanLineMaterial]);

  // Cleanup: clear uniforms, dispose textures and scene on unmount
  useEffect(() => {
    return () => {
      try {
        const uniforms = scanLineUniformsRef.current;
        if (uniforms) {
          uniforms.uTexture.value = null;
          uniforms.uEnvMap.value = null;
        }

        // Keep textures alive in shared cache to avoid black textures on remount
        texturesRef.current = {};

        try { disposeNode(scene); } catch (_) {}
      } catch (e) {
        console.warn('ScreenModel cleanup error:', e);
      }
    };
  }, [scene, envTexture]);

  return (
    <>
      <primitive object={scene} castShadow receiveShadow />
      <pointLight
        ref={pointLightRef}
        position={[0, 0, 0.035]} // Same Z position as screen
        intensity={lightsOn ? 1.5 : 0.0}
        distance={2.5}
        color={0x0066ff}
      />
    </>
  );
}

// Camera controller for subtle orbiting effect
function CameraController({ isZoomed, onZoomComplete, onZoomProgress, loadingFinished = false, onIntroComplete, onOrientationUpdate = undefined, isMobile = false, isVisible = true }) {
  const { camera, invalidate } = useThree();
  const baseCameraY = isMobile ? 1.75 : 1.5;
  const baseLookAtY = isMobile ? -0.65 : 0;
  const baseCameraZ = isMobile ? 5.5 : 5;
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const isInitialized = useRef(false);
  // Track the most recent lookAt target so animations start from the true current orientation
  const currentLookAtRef = useRef(new THREE.Vector3(0, 0, 0));
  const deviceOrientationActiveRef = useRef(false);
  const lastOrientationSendRef = useRef(0);
  // Loading-driven intro drop animation
  const loadingAnimRef = useRef({
    isAnimating: false,
    startTime: 0,
    startPosition: new THREE.Vector3(),
    targetPosition: new THREE.Vector3(0, baseCameraY, baseCameraZ),
    startLookAt: new THREE.Vector3(),
    targetLookAt: new THREE.Vector3(0, baseLookAtY, 0),
    durationMs: 900,
    hasDropped: false
  });
  const zoomRef = useRef({
    isAnimating: false,
    startTime: 0,
    startPosition: new THREE.Vector3(),
    targetPosition: new THREE.Vector3(),
    startLookAt: new THREE.Vector3(),
    targetLookAt: new THREE.Vector3(),
    lastZoomState: null,
    zoomingOut: false,
    disableMouseUntil: 0
  });
  useEffect(() => {
    // Only initialize camera position once
    if (!isInitialized.current) {
      if (isZoomed) {
        camera.position.set(0, 0.65, 1.3);
        camera.lookAt(0, 0.5, 0);
        currentLookAtRef.current.set(0, 0.5, 0);
      } else {
        // Start farther back (zoomed out) while loading overlay hasn't finished
        if (!loadingFinished) {
          camera.position.set(0, baseCameraY, 10.0);
          camera.lookAt(0, 0, 0);
          currentLookAtRef.current.set(0, baseLookAtY, 0);
        } else {
          camera.position.set(0, baseCameraY, baseCameraZ);
          camera.lookAt(0, 0, 0);
          currentLookAtRef.current.set(0, baseLookAtY, 0);
        }
      }
      isInitialized.current = true;
      // Respect initial zoom state and avoid triggering immediate zoom-in animation
      zoomRef.current.lastZoomState = isZoomed;
    }

    // Handle zoom animation - only trigger when zoom state actually changes
    if (isZoomed !== undefined && isZoomed !== zoomRef.current.lastZoomState) {
      zoomRef.current.lastZoomState = isZoomed;

      if (isZoomed && !zoomRef.current.isAnimating) {
        // Start zoom in animation
        zoomRef.current.isAnimating = true;
        zoomRef.current.startTime = Date.now();
        zoomRef.current.startPosition.copy(camera.position);
        zoomRef.current.targetPosition.set(0, 0.65, 1.3); // Close to screen (x, y, z)
        // Start from whatever we're currently looking at
        zoomRef.current.startLookAt.copy(currentLookAtRef.current);
        zoomRef.current.targetLookAt.set(0, 0.5, 0);
        zoomRef.current.zoomingOut = false;
      } else if (!isZoomed && !zoomRef.current.isAnimating) {
        // Start zoom out animation
        zoomRef.current.isAnimating = true;
        zoomRef.current.startTime = Date.now();
        zoomRef.current.startPosition.copy(camera.position);
        zoomRef.current.targetPosition.set(0, baseCameraY, baseCameraZ); // Back to original
        // Start from whatever we're currently looking at
        zoomRef.current.startLookAt.copy(currentLookAtRef.current);
        zoomRef.current.targetLookAt.set(0, baseLookAtY, 0);
        zoomRef.current.zoomingOut = true;
      }
    }
  }, [isZoomed, camera, loadingFinished]);

  // Trigger camera drop when overlay signals loading completion (only once)
  useEffect(() => {
    if (loadingFinished && !loadingAnimRef.current.hasDropped && !isZoomed) {
      loadingAnimRef.current.isAnimating = true;
      loadingAnimRef.current.startTime = Date.now();
      loadingAnimRef.current.startPosition.copy(camera.position);
      loadingAnimRef.current.startLookAt.copy(currentLookAtRef.current);
      // Animate forward along Z only (no vertical drop)
      loadingAnimRef.current.targetPosition.set(0, baseCameraY, baseCameraZ);
      loadingAnimRef.current.targetLookAt.set(0, baseLookAtY, 0);
      // kick a render in case frameloop is demand
      if (invalidate) invalidate();
    }
  }, [loadingFinished, isZoomed, camera, baseCameraY, baseCameraZ, baseLookAtY, invalidate]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      // Ignore mouse input during zoom animation and a brief cooldown afterwards
      const now = Date.now();
      // Also ignore until the loading drop animation has fully completed
      if (!loadingAnimRef.current.hasDropped || loadingAnimRef.current.isAnimating || zoomRef.current.isAnimating || now < (zoomRef.current.disableMouseUntil || 0)) {
        return;
      }
      // Normalize mouse coordinates to -1 to 1 range
      const x = (event.clientX / window.innerWidth) * 2 - 1;
      const y = -(event.clientY / window.innerHeight) * 2 + 1;

      // Set target position with constant sensitivity
      const speedScale = 1.0;
      const sensitivity = 0.7 / speedScale;
      targetRef.current.x = -x * sensitivity;
      targetRef.current.y = -y * sensitivity;
    };

    window.addEventListener('mousemove', handleMouseMove);
    // On breakpoint change, reset scale-dependent layout by forcing a tiny reflow tick
    const handleResize = () => {
      // Defer to allow layout queries to settle
      requestAnimationFrame(() => {
        if (invalidate) invalidate();
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, [camera, invalidate]);

  // Simplified device tilt: direct mapping without permission flow
  useEffect(() => {
    const handleOrientation = (event) => {
      const beta = typeof event.beta === 'number' ? event.beta : 0;
      const gamma = typeof event.gamma === 'number' ? event.gamma : 0;
      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
      const betaClamped = clamp(beta, -30, 30);
      const gammaClamped = clamp(gamma, -30, 30);
      const nx = gammaClamped / 30;
      const ny = betaClamped / 30;
      const speedScale = 1.0;
      const sensitivity = 0.7 / speedScale;
      targetRef.current.x = nx * sensitivity;
      targetRef.current.y = ny * sensitivity;
      deviceOrientationActiveRef.current = true;
      if (onOrientationUpdate) onOrientationUpdate('device', { alpha: (typeof event.alpha === 'number' ? event.alpha : null), beta, gamma });
    };
    window.addEventListener('deviceorientation', handleOrientation, { passive: true });
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [onOrientationUpdate]);

  useFrame(() => {
    if (!isVisible) return;
    // Handle zoom animation
    if (zoomRef.current.isAnimating) {
      const elapsed = Date.now() - zoomRef.current.startTime;
      const speedScale = 1.0;
      const duration = 500 * speedScale; // constant duration
      const progress = Math.min(elapsed / duration, 1);

      // Use smooth easing
      const easeProgress = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      // Notify zoom progress for effects
      if (onZoomProgress) {
        onZoomProgress({ progress: easeProgress, zoomingOut: zoomRef.current.zoomingOut });
      }


      // Interpolate position
      camera.position.lerpVectors(
        zoomRef.current.startPosition,
        zoomRef.current.targetPosition,
        easeProgress
      );

      // Interpolate look at
      const currentLookAt = new THREE.Vector3().lerpVectors(
        zoomRef.current.startLookAt,
        zoomRef.current.targetLookAt,
        easeProgress
      );
      camera.lookAt(currentLookAt);
      // Keep our tracked lookAt target in sync during animation
      currentLookAtRef.current.copy(currentLookAt);

      // Check if animation is complete
      if (progress >= 1) {
        zoomRef.current.isAnimating = false;
        // After zoom completes, add a short cooldown where mouse input is ignored
        // and reset the orbit target/current so we resume from front-center smoothly.
        const cooldownMs = 200; // prevents immediate snap to last mouse position
        zoomRef.current.disableMouseUntil = Date.now() + cooldownMs;
        if (zoomRef.current.zoomingOut) {
          // Reset orbit state to neutral so camera doesn't jump to stale mouse targets
          targetRef.current.x = 0;
          targetRef.current.y = 0;
          currentRef.current.x = 0;
          currentRef.current.y = 0;
        }
        if (onZoomComplete) {
          onZoomComplete({ zoomingOut: zoomRef.current.zoomingOut });
        }
      }
      return; // Skip mouse movement during zoom
    }

    // If loading just finished and drop hasn't run, initialize drop here
    if (!isZoomed && loadingFinished && !loadingAnimRef.current.hasDropped && !loadingAnimRef.current.isAnimating) {
      loadingAnimRef.current.isAnimating = true;
      loadingAnimRef.current.startTime = Date.now();
      loadingAnimRef.current.startPosition.copy(camera.position);
      loadingAnimRef.current.startLookAt.copy(currentLookAtRef.current);
      // Animate forward along Z only (no vertical drop)
      loadingAnimRef.current.targetPosition.set(0, baseCameraY, baseCameraZ);
      loadingAnimRef.current.targetLookAt.set(0, baseLookAtY, 0);
    }

    // Handle loading intro drop animation
    if (loadingAnimRef.current.isAnimating) {
      const elapsed = Date.now() - loadingAnimRef.current.startTime;
      const t = Math.min(elapsed / loadingAnimRef.current.durationMs, 1);
      // easeInOutCubic for smoother start and end
      const ease = t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;

      // Interpolate position
      camera.position.lerpVectors(
        loadingAnimRef.current.startPosition,
        loadingAnimRef.current.targetPosition,
        ease
      );

      // Interpolate look at
      const lookAtNow = new THREE.Vector3().lerpVectors(
        loadingAnimRef.current.startLookAt,
        loadingAnimRef.current.targetLookAt,
        ease
      );
      camera.lookAt(lookAtNow);
      currentLookAtRef.current.copy(lookAtNow);

      if (t >= 1) {
        loadingAnimRef.current.isAnimating = false;
        loadingAnimRef.current.hasDropped = true;
        // small cooldown to avoid mouse snap
        zoomRef.current.disableMouseUntil = Date.now() + 300;
        if (onIntroComplete) {
          try { onIntroComplete(); } catch (_) {}
        }
      }
      if (invalidate) invalidate();
      return; // Skip mouse movement during loading drop
    }

    // Only apply mouse-based movement if not zoomed, after cooldown, and only AFTER loading drop has completed
    const now = Date.now();
    if (!isZoomed && loadingAnimRef.current.hasDropped && now >= (zoomRef.current.disableMouseUntil || 0)) {
      const mouseMoved = Math.abs(targetRef.current.x) > 0.01 || Math.abs(targetRef.current.y) > 0.01;

      if (mouseMoved) {
        // Smooth interpolation towards target
        const speedScale = 1.0;
        const followLerp = 0.05 / speedScale; // constant follow speed
        currentRef.current.x += (targetRef.current.x - currentRef.current.x) * followLerp;
        currentRef.current.y += (targetRef.current.y - currentRef.current.y) * followLerp;

        // Calculate orbital position
        const radius = 5; // Match the initial camera distance
        const angleX = currentRef.current.x * 0.5; // Increased horizontal orbit range
        const angleY = currentRef.current.y * 1; // Further reduced orbit range

        // Update camera position
        camera.position.x = Math.sin(angleX) * radius;
        camera.position.y = baseCameraY + angleY;
        camera.position.z = isMobile ? baseCameraZ : Math.cos(angleX) * radius;

        // Always look at the center
        camera.lookAt(0, baseLookAtY, 0);
        currentLookAtRef.current.set(0, baseLookAtY, 0);
      } else {
        // No mouse movement: keep current position; do not auto-drift before/after drop
      }
    }

    // Fallback/camera orientation reporting: when device orientation is not active, report camera Euler angles periodically
    if (!deviceOrientationActiveRef.current && typeof onOrientationUpdate === 'function') {
      const sendIntervalMs = 250;
      if (now - (lastOrientationSendRef.current || 0) >= sendIntervalMs) {
        const toDeg = (r) => r * 180 / Math.PI;
        const alpha = toDeg(camera.rotation.y); // yaw
        const beta = toDeg(camera.rotation.x);  // pitch
        const gamma = toDeg(camera.rotation.z); // roll
        try { onOrientationUpdate('camera', { alpha, beta, gamma }); } catch (_) {}
        lastOrientationSendRef.current = now;
      }
    }
  });

  return null;
}

// Toggles wireframe on all mesh materials in the scene
function WireframeToggler({ enabled }) {
  const { scene } = useThree();
  useEffect(() => {
    if (!scene) return;
    scene.traverse((obj) => {
      if (obj.isMesh && obj.material) {
        const mat = obj.material;
        if (Array.isArray(mat)) {
          mat.forEach((m) => { if (m && 'wireframe' in m) m.wireframe = !!enabled; });
        } else if ('wireframe' in mat) {
          mat.wireframe = !!enabled;
        }
      }
    });
  }, [scene, enabled]);
  // Ensure meshes that load after mount also get updated while enabled
  useFrame(() => {
    if (!enabled || !scene) return;
    scene.traverse((obj) => {
      if (obj.isMesh && obj.material) {
        const mat = obj.material;
        if (Array.isArray(mat)) {
          mat.forEach((m) => { if (m && 'wireframe' in m && !m.wireframe) m.wireframe = true; });
        } else if ('wireframe' in mat && !mat.wireframe) {
          mat.wireframe = true;
        }
      }
    });
  });
  return null;
}

function Model({ isZoomed, onZoomComplete, onStatsUpdate, selectedTexture, infoActiveId, onZoomFxProgress, rippleDisabled = false, introActive = false, loadingFinished = false, onIntroComplete = undefined, onOrientationUpdate = undefined, wireframe = false, lightsOn = true, gridVisible = true, onReadyForDisplay = undefined }) {
  const [rippleAmount, setRippleAmount] = useState(0);
  const [screenFade, setScreenFade] = useState(0);
  const [infoPanelAnchor, setInfoPanelAnchor] = useState(null);
  const { windowSize } = useBrowserInfo();
  const isMobile = windowSize.width <= 768;
  const isMobileRef = useRef(isMobile);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);

  // Track page visibility to pause rendering/animations and lower DPR when hidden
  const [isVisible, setIsVisible] = useState(typeof document === 'undefined' ? true : document.visibilityState === 'visible');
  useEffect(() => {
    const onVisibility = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const handleZoomProgress = ({ progress, zoomingOut }) => {
    if (!zoomingOut) {
      // Start ripple near the end of zoom-in
      const start =  0.15; // slight delay to ripple start
      let t = (progress - start) / (1 - start);
      if (t < 0) t = 0; if (t > 1) t = 1;
      const eased = t * t * (3 - 2 * t); // smoothstep
      setRippleAmount(rippleDisabled ? 0 : eased);
      // Ensure fade is always > 0 while zooming in so shader sees a non-zero value
      setScreenFade(Math.max(progress, 0.001));
      if (onZoomFxProgress) onZoomFxProgress(eased);
    } else {
      setRippleAmount(0);
      setScreenFade(0);
      if (onZoomFxProgress) onZoomFxProgress(0);
    }
  };

  // If zoom toggles on but progress callback hasn't fired yet, nudge fade above zero
  useEffect(() => {
    if (isZoomed) {
      setScreenFade((prev) => Math.max(prev, 0.001));
    } else {
      setScreenFade(0);
    }
  }, [isZoomed]);

  return (
    <div className="model-container">
      <Canvas
        camera={{ position: [0, 2.5, 5], fov: 50 }}
        style={{ width: '100vw', height: '100vh' }}
        frameloop={isVisible ? "always" : "demand"}
        dpr={isVisible ? [1, 2] : 1} // play around with this value to get the best performance
        shadows={{ type: THREE.VSMShadowMap }}
        gl={{
          antialias: true,
          powerPreference: 'low-power',
          precision: 'lowp',
          alpha: true, // could use this to switch to dark mode
          stencil: false,
          depth: true,
          failIfMajorPerformanceCaveat: true,
          outputColorSpace: THREE.LinearSRGBColorSpace,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 0.9
        }}
      >

        {/* Lighting */}
        <ambientLight intensity={lightsOn ? 0.35 : 0.0} />
        <directionalLight
          position={[2, 6, 5]} // key light from front-right above (positive Z = front)
          intensity={lightsOn ? 2.2 : 0.0}
          castShadow={!isMobile}
          shadow-mapSize={isMobile ? [1024, 1024] : [2048, 2048]}
          shadow-radius={20}
          shadow-bias={-0.0001}
          shadow-normalBias={0.02}
          shadow-camera-near={0.1}
          shadow-camera-far={50}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
        />
        {/* Fill and rim lights */}
        <directionalLight position={[4, 2.5, 4]} intensity={lightsOn ? 0.5 : 0.0} />
        <hemisphereLight args={[0x87CEEB, 0x8B4513, lightsOn ? 0.6 : 0.0]} />

        {/* Grid helper for reference */}
        {gridVisible && (
          <gridHelper args={[30, 60, 0xf5f5f5, 0xf5f5f5]} position={[0, -0.89 + 0.0909, 0]} />
        )}

        {/* Ground Plane */}
        <GroundPlane />

        {/* Models */}
        <Suspense fallback={null}>
          <MacModel />
          <ScreenModel selectedTexture={selectedTexture} rippleAmount={rippleAmount} isZoomed={isZoomed} screenFadeAmount={screenFade} introActive={introActive} onAnchorReady={setInfoPanelAnchor} mobilePerfMode={isMobile} scanlinesEnabled={SCANLINES_ENABLED} glitchEnabled={GLITCH_ENABLED} reflectionsEnabled={REFLECTIONS_ENABLED} isVisible={isVisible} lightsOn={lightsOn} onReadyForDisplay={onReadyForDisplay} />
          {!isMobile && (
            <>
              <MugModel />
            </>
          )}
        </Suspense>

        {/* Camera‑attached HUD (hidden on mobile) */}
        {!isMobile && (
          <group position={[0, 0, 0]}>
            <InfoPanel3D
              activeId={infoActiveId}
              followObject={infoPanelAnchor}
              position={[1.25, 0.8, 0.9]}
              offset={[0.75, 0.0, 0.0]}
              tiltStrength={0}
            />
          </group>
        )}

        {/* Camera Controller */}
        <CameraController isZoomed={isZoomed} onZoomComplete={onZoomComplete} onZoomProgress={handleZoomProgress} loadingFinished={loadingFinished} onIntroComplete={onIntroComplete} onOrientationUpdate={onOrientationUpdate} isMobile={isMobile} isVisible={isVisible} />

        {/* Wireframe toggler (auto-on during loading) */}
        <WireframeToggler enabled={wireframe || !loadingFinished} />

        {/* Debug memory overlay removed */}
      </Canvas>
    </div>
  );
}

// Memory cleanup components removed for now

export default function ModelWrapper(props) {
  // Removed GLTF cache clear on unmount for now
  return <Model {...props} />;
}
